import { useEffect, useState, lazy, Suspense } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SupplierForm from "@/components/SupplierForm";
import { SupplierQuiz } from "@/components/supplier-quiz";
import { QuizPrefillData } from "@/components/supplier-quiz/QuizLeadForm";

// Import above-fold sections directly (not from barrel to avoid eager loading)
import SupplierHero from "@/components/supplier-landing/SupplierHero";
import LogoCloudSection from "@/components/supplier-landing/LogoCloudSection";
import { homeFaqs } from "@/data/homeFaqs";

import HighlightBanner from "@/components/supplier-landing/HighlightBanner";
import AiPoweredTagline from "@/components/supplier-landing/AiPoweredTagline";
import CardapioDigitalAdvantages from "@/components/CardapioDigitalAdvantages";

// Lazy load below-fold sections for faster initial paint
const SolutionsGrid = lazy(() => import("@/components/supplier-landing/SolutionsGrid"));

const DigitalMenuShowcase = lazy(() => import("@/components/supplier-landing/DigitalMenuShowcase"));

const StatsBanner = lazy(() => import("@/components/supplier-landing/StatsBanner"));
const PaymentsShowcaseBox = lazy(() => import("@/components/supplier-landing/PaymentsShowcaseBox"));
const FAQSection = lazy(() => import("@/components/supplier-landing/FAQSection"));

const SectionFallback = () => (
  <div className="py-24 flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const SupplierRegistration = () => {
  const [showForm, setShowForm] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [prefillData, setPrefillData] = useState<QuizPrefillData | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const seoTitle = isHome
    ? "Gestão de Eventos - Feito para o seu Buffet"
    : "Agende uma conversa";
  const seoDescription = isHome
    ? "Plataforma para buffets venderem mais e gerenciarem propostas, CRM, pagamentos, contratos e eventos em um só lugar."
    : "Sistema completo para buffets: proposta digital, pagamento, contrato, CRM e WhatsApp integrados. Venda mais e organize seu negócio em um só lugar.";
  // Canônica dinâmica baseada na rota atual: raiz para a Home e
  // /cadastro-fornecedor para a página de cadastro.
  const canonical = `https://ameup.com.br${location.pathname}`;

  // PagBank OAuth fallback: se o PagBank retornar para "/" (em vez da Redirect URI correta),
  // capturamos os parâmetros e redirecionamos para a aba de Finanças preservando o code/state.
  useEffect(() => {
    const hasOAuthPayload =
      searchParams.has('code') &&
      (searchParams.has('state') || searchParams.has('scope'));
    if (!hasOAuthPayload) return;
    const target = `/auth/pagbank/callback?${searchParams.toString()}`;
    window.location.replace(target);
  }, [searchParams]);

  useEffect(() => {
    // Auto-open form when accessing /cadastro-fornecedor or ?open=form
    const isCadastroRoute = window.location.pathname === "/cadastro-fornecedor";
    const isOpenForm = searchParams.get("open") === "form";

    if (isCadastroRoute || isOpenForm) {
      setShowForm(true);
      if (isOpenForm) {
        setSearchParams((params) => {
          const nextParams = new URLSearchParams(params);
          nextParams.delete("open");
          return nextParams;
        }, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);


  const handleShowForm = () => {
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setPrefillData(undefined);
  };

  const handleShowQuiz = () => {
    setShowQuiz(true);
  };

  const handleCloseQuiz = () => {
    setShowQuiz(false);
  };

  const handleScheduleFromQuiz = (data: QuizPrefillData) => {
    setPrefillData(data);
    setShowForm(true);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonical={canonical}
        ogType="website"
        ogTitle={seoTitle}
        ogDescription={seoDescription}
        faqs={isHome ? homeFaqs : undefined}
      />

      <Navbar />

      <main className="flex-grow">
        <SupplierHero onCadastrarClick={handleShowForm} onQuizClick={handleShowQuiz} />
        <LogoCloudSection />
        <AiPoweredTagline />
        <CardapioDigitalAdvantages />
        <Suspense fallback={<SectionFallback />}>
          <DigitalMenuShowcase />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <SolutionsGrid />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <PaymentsShowcaseBox />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <FAQSection />
        </Suspense>
      </main>

      <Footer hidePaymentMethods={true} />

      <SupplierForm isOpen={showForm} onClose={handleCloseForm} prefillData={prefillData} />
      <SupplierQuiz isOpen={showQuiz} onClose={handleCloseQuiz} onScheduleMeeting={handleScheduleFromQuiz} />
    </div>
  );
};

export default SupplierRegistration;
